import { useQuery } from '@tanstack/react-query'
import { useDebounce } from './useDebounce'
import { searchSkus, type SkuSearchResponse } from '@/api/skuSearch'
import { queryKeys } from '@/lib/queryKeys'

interface UseSkuSearchParams {
  query: string
  debounceMs?: number
  limit?: number
  enabled?: boolean
}

/**
 * Hook for searching SKUs with debounced query
 * @param params - Search parameters
 */
export function useSkuSearch({
  query,
  debounceMs = 300,
  limit = 20,
  enabled = true,
}: UseSkuSearchParams) {
  const debouncedQuery = useDebounce(query, debounceMs)

  return useQuery<SkuSearchResponse>({
    queryKey: queryKeys.skuSearch.byQuery(debouncedQuery),
    queryFn: ({ signal }) =>
      searchSkus(
        {
          q: debouncedQuery,
          limit,
        },
        signal
      ),
    enabled: enabled && debouncedQuery.length >= 1,
    staleTime: 1000 * 60, // Cache for 1 minute
  })
}
